import React, { useEffect, useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, Search, CheckCircle, AlertTriangle, Shield, Building2, Ship, Server } from 'lucide-react'
import { projectsApi, SystemListItem } from '@/lib/projectsApi'
import { useToast } from '@/contexts/ToastContext'
import { Button } from '@/components/ui/Button'
import { useTranslation } from 'react-i18next'

type StatusFilter = 'all' | 'not_assessed' | 'cbs' | 'non_cbs'

export const SystemsPage: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { error: showError } = useToast()
  const [systems, setSystems] = useState<SystemListItem[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')
  const [projectFilter, setProjectFilter] = useState('')
  const [vendorFilter, setVendorFilter] = useState('')

  useEffect(() => {
    (async () => {
      try {
        setSystems(await projectsApi.listSystems())
      } catch (e: any) {
        showError(e.message ?? t('systemsPage.loadFailed'))
      } finally {
        setLoading(false)
      }
    })()
  }, [showError, t])

  const projectOptions = useMemo(() => {
    const m = new Map<string, string>()
    for (const s of systems) {
      if (!m.has(s.project_id)) {
        m.set(s.project_id, `${s.shipyard_name ?? '?'} / ${s.hull_number}`)
      }
    }
    return [...m.entries()].sort((a, b) => a[1].localeCompare(b[1]))
  }, [systems])

  const vendorOptions = useMemo(() =>
    [...new Set(systems.map(s => s.vendor_name_raw).filter(Boolean))].sort() as string[]
  , [systems])

  const filtered = useMemo(() => {
    const term = search.toLowerCase().trim()
    return systems.filter(s => {
      if (statusFilter !== 'all' && s.assessment_status !== statusFilter) return false
      if (projectFilter && s.project_id !== projectFilter) return false
      if (vendorFilter && s.vendor_name_raw !== vendorFilter) return false
      if (term) {
        const hay = [s.system_name, s.system_description, s.function_name, s.vendor_name_raw, s.hull_number, s.shipyard_name]
          .filter(Boolean).join(' ').toLowerCase()
        if (!hay.includes(term)) return false
      }
      return true
    })
  }, [systems, search, statusFilter, projectFilter, vendorFilter])

  const stats = useMemo(() => ({
    total: systems.length,
    assessed: systems.filter(s => s.assessment_status !== 'not_assessed').length,
    cbs: systems.filter(s => s.assessment_status === 'cbs').length,
    non_cbs: systems.filter(s => s.assessment_status === 'non_cbs').length,
  }), [systems])

  const groupedByProject = useMemo(() => {
    const m = new Map<string, { label: string; items: SystemListItem[] }>()
    for (const s of filtered) {
      const label = `${s.shipyard_name ?? '?'} / ${s.hull_number}`
      if (!m.has(s.project_id)) m.set(s.project_id, { label, items: [] })
      m.get(s.project_id)!.items.push(s)
    }
    return [...m.entries()].sort((a, b) => a[1].label.localeCompare(b[1].label))
  }, [filtered])

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 text-slate-900 dark:text-white">
      <div className="mx-auto px-4 py-4 max-w-[1400px]">

        {/* Header */}
        <div className="flex items-center gap-3 mb-3">
          <Button variant="secondary" size="sm" onClick={() => navigate('/dashboard')}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-lg font-semibold flex items-center gap-2">
              <Shield className="h-5 w-5" />
              {t('systemsPage.title')}
            </h1>
            <p className="text-xs text-slate-400 dark:text-slate-500">{t('systemsPage.subtitle')}</p>
          </div>
        </div>

        {/* Stats bar with clickable filter tiles */}
        <div className="grid grid-cols-4 gap-2 mb-3">
          <button
            onClick={() => setStatusFilter('all')}
            className={`text-left px-3 py-2 rounded-lg border ${
              statusFilter === 'all' ? 'border-slate-400 dark:border-slate-500 bg-white dark:bg-slate-800' : 'border-slate-200 dark:border-slate-700 bg-white/60 dark:bg-slate-800/40'
            }`}
          >
            <div className="text-[10px] text-slate-400 uppercase">{t('common.total')}</div>
            <div className="text-lg font-semibold">{stats.total}</div>
          </button>
          <button
            onClick={() => setStatusFilter('not_assessed')}
            className={`text-left px-3 py-2 rounded-lg border ${
              statusFilter === 'not_assessed' ? 'border-slate-400 dark:border-slate-500 bg-white dark:bg-slate-800' : 'border-slate-200 dark:border-slate-700 bg-white/60 dark:bg-slate-800/40'
            }`}
          >
            <div className="text-[10px] text-slate-400 uppercase">{t('systemsPage.notAssessed')}</div>
            <div className="text-lg font-semibold text-slate-500">{stats.total - stats.assessed}</div>
          </button>
          <button
            onClick={() => setStatusFilter('cbs')}
            className={`text-left px-3 py-2 rounded-lg border ${
              statusFilter === 'cbs' ? 'border-amber-400 bg-amber-50 dark:bg-amber-900/20' : 'border-slate-200 dark:border-slate-700 bg-white/60 dark:bg-slate-800/40'
            }`}
          >
            <div className="text-[10px] text-slate-400 uppercase">CBS</div>
            <div className="text-lg font-semibold text-amber-600 dark:text-amber-400">{stats.cbs}</div>
          </button>
          <button
            onClick={() => setStatusFilter('non_cbs')}
            className={`text-left px-3 py-2 rounded-lg border ${
              statusFilter === 'non_cbs' ? 'border-emerald-400 bg-emerald-50 dark:bg-emerald-900/20' : 'border-slate-200 dark:border-slate-700 bg-white/60 dark:bg-slate-800/40'
            }`}
          >
            <div className="text-[10px] text-slate-400 uppercase">Non-CBS</div>
            <div className="text-lg font-semibold text-emerald-600 dark:text-emerald-400">{stats.non_cbs}</div>
          </button>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-2 mb-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="h-3.5 w-3.5 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              placeholder={t('systemsPage.searchPlaceholder')}
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full h-9 pl-9 pr-3 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder:text-slate-400"
            />
          </div>
          <select value={projectFilter} onChange={e => setProjectFilter(e.target.value)}
                  className="h-9 px-3 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white min-w-[150px]">
            <option value="">{t('systemsPage.allProjects')}</option>
            {projectOptions.map(([id, label]) => <option key={id} value={id}>{label}</option>)}
          </select>
          <select value={vendorFilter} onChange={e => setVendorFilter(e.target.value)}
                  className="h-9 px-3 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white min-w-[150px]">
            <option value="">{t('systemsPage.allVendors')}</option>
            {vendorOptions.map(v => <option key={v} value={v}>{v}</option>)}
          </select>
          {(search || statusFilter !== 'all' || projectFilter || vendorFilter) && (
            <button onClick={() => { setSearch(''); setStatusFilter('all'); setProjectFilter(''); setVendorFilter('') }}
                    className="h-9 px-3 text-xs text-slate-400 hover:text-slate-600 dark:hover:text-slate-300">
              {t('common.clear')}
            </button>
          )}
        </div>

        {/* Count */}
        <p className="text-[11px] text-slate-400 mb-2">
          {filtered.length === systems.length
            ? t('systemsPage.systemCount', { count: systems.length })
            : t('systemsPage.filteredCount', { filtered: filtered.length, total: systems.length })}
        </p>

        {/* Content */}
        {loading ? (
          <div className="py-16 text-center text-slate-400">{t('systemsPage.loading')}</div>
        ) : filtered.length === 0 ? (
          <div className="py-16 text-center">
            <Server className="h-10 w-10 text-slate-300 dark:text-slate-600 mx-auto mb-3" />
            <p className="text-sm text-slate-400">{t('systemsPage.noMatch')}</p>
          </div>
        ) : (
          <div className="space-y-4">
            {groupedByProject.map(([projectId, group]) => (
              <div key={projectId}>
                <div className="flex items-center gap-2 pb-1.5 mb-1.5 border-b border-slate-200 dark:border-slate-700">
                  <Ship className="h-3.5 w-3.5 text-slate-400" />
                  <span className="text-xs font-medium">{group.label}</span>
                  <span className="text-[10px] text-slate-400">{t('systemsPage.systemCount', { count: group.items.length })}</span>
                </div>
                <div className="border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden bg-white dark:bg-slate-800/30">
                  {group.items.map((s, i) => (
                    <button
                      key={s.id}
                      onClick={() => navigate(`/projects/${s.project_id}/systems/${s.id}`)}
                      className={`w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-slate-50 dark:hover:bg-slate-800/60 ${
                        i > 0 ? 'border-t border-slate-100 dark:border-slate-700/50' : ''
                      }`}
                    >
                      {/* Status icon */}
                      <div className="flex-shrink-0">
                        {s.assessment_status === 'cbs' ? (
                          <div className="w-6 h-6 rounded-full bg-amber-100 dark:bg-amber-900/40 flex items-center justify-center">
                            <AlertTriangle className="h-3 w-3 text-amber-600 dark:text-amber-400" />
                          </div>
                        ) : s.assessment_status === 'non_cbs' ? (
                          <div className="w-6 h-6 rounded-full bg-emerald-100 dark:bg-emerald-900/40 flex items-center justify-center">
                            <CheckCircle className="h-3 w-3 text-emerald-600 dark:text-emerald-400" />
                          </div>
                        ) : (
                          <div className="w-6 h-6 rounded-full bg-slate-100 dark:bg-slate-700 flex items-center justify-center">
                            <Shield className="h-3 w-3 text-slate-400" />
                          </div>
                        )}
                      </div>

                      {/* Name + function */}
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-slate-900 dark:text-white truncate">
                          {s.system_name || s.system_description || t('systemsPage.unnamed')}
                        </div>
                        <div className="text-[10px] text-slate-400 truncate">{s.function_name || '—'}</div>
                      </div>

                      {/* Vendor */}
                      <div className="hidden sm:block w-40 min-w-0">
                        <div className="text-xs text-slate-600 dark:text-slate-400 truncate flex items-center gap-1">
                          <Building2 className="h-3 w-3 flex-shrink-0 text-slate-300" />
                          {s.vendor_name_raw || '—'}
                        </div>
                      </div>

                      {/* Status badge */}
                      <div className="hidden md:block w-24 text-right">
                        {s.assessment_status === 'cbs' ? (
                          <span className="text-[9px] font-semibold px-2 py-0.5 rounded-full bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300">CBS</span>
                        ) : s.assessment_status === 'non_cbs' ? (
                          <span className="text-[9px] font-semibold px-2 py-0.5 rounded-full bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300">Non-CBS</span>
                        ) : (
                          <span className="text-[9px] px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-700 text-slate-400">{t('systemsPage.notAssessed')}</span>
                        )}
                      </div>

                      {/* Type approval */}
                      <div className="hidden lg:block w-16 text-right">
                        {s.type_approval_status && (
                          <span className="text-[9px] text-slate-500 dark:text-slate-400">{s.type_approval_status}</span>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
